import { APP_ID_SINGLE_ZONE_SCHEDULER } from "constant/application";
import {
  MEDIA_ENTRY_APPLICATION,
  MEDIA_ENTRY_CONTENT_GROUP,
  MEDIA_ENTRY_USER_CONTENT,
  MEDIA_ENTRY_WEBPAGE,
} from "fleet/constant/media-library";
import { PRIORITY_FALLBACK, PRIORITY_MAIN } from "fleet/constant/strategy-v2";
import {
  isImage,
  strategyLevelAppToMediaEntry,
  strategyLevelCGToMediaEntry,
  strategyLevelUCToMediaEntry,
  strategyLevelWebpageToMediaEntry,
} from "fleet/view/media-library/utils";
import cloneDeep from "lodash/cloneDeep";
import uniqueId from "lodash/uniqueId";
import PropTypes from "prop-types";
import React from "react";
import { Link } from "react-router-dom";
import * as App from "redux-store/applications/selectors";
import { defaultArray, findByProperty, getById } from "redux-store/selectors";
import { sortByValueAsc } from "util/sort-by-prop";
import { utcToLocal } from "util/time";
import { VIEW } from "view/views";

const getSzScheduler = findByProperty.bind(
  null,
  "name",
  APP_ID_SINGLE_ZONE_SCHEDULER,
);

/**
 * Strategies from the API are a nested structure. We need to flatten them
 * to work more cleanly with Redux.
 *
 * normalized strategy looks like:
 * {
 *    account_id: number,
 *    archived: bool,
 *    create_date: string,
 *    id: number,
 *    name: string,
 *    strategy: {
 *      name: string,
 *      priorities: array,
 *      scheduler: {
 *        appId: number,
 *      }
 *    }
 * }
 *
 * @typedef {Object} Scheduler
 * @property {number} appId Application Id
 *
 * @typedef {Object} DenormalizedStrategy
 * @property {number} account_id Account id
 * @property {boolean} archived Archived
 * @property {string} create_date Create Date
 * @property {Array} fallback Fallback priority
 * @property {Array} main Main Priority
 * @property {number} id id
 * @property {Scheduler} scheduler scheduler
 *
 * @param {Object} applications - Application Store
 * @param {Object} userContents - User Content Store
 * @param {Object} contentGroups - Content Group Store
 * @param {Object} rawStrategy - Strategy
 * @return {DenormalizedStrategy} - denormalized strategy
 */
function denormalizeStrategy(
  applications,
  userContents,
  contentGroups,
  rawStrategy,
) {
  if (!rawStrategy) {
    return null;
  }

  const strategy = cloneDeep(rawStrategy);
  const strategyJson = strategy.strategy;
  delete strategy.strategy;
  const stores = {
    applications,
    userContents,
    contentGroups,
  };

  const spotDenormalizer = spotToMediaEntry.bind(null, stores);

  const mainPriority = strategyJson.priorities[0] || [];
  const fallbackPriority = strategyJson.priorities[1] || [];
  const main = mainPriority.map(spotDenormalizer);
  const fallback = fallbackPriority.map(spotDenormalizer);

  return {
    ...strategy,
    main,
    fallback,
    scheduler: strategyJson.scheduler,
  };
}

function getScheduler(applications, scheduler) {
  if (scheduler) {
    scheduler = getById(applications, scheduler.appId);
  }

  if (!App.installed(scheduler)) {
    scheduler = getSzScheduler(applications);
  }

  return scheduler;
}

function normalizeStrategy(applications, denormalized) {
  const { main, name, fallback, scheduler } = denormalized;

  return {
    name,
    scheduler: getScheduler(applications, scheduler).id,
    priorities: [main.map(normalizeSpot), fallback.map(normalizeSpot)],
  };
}

function spotToMediaEntry(stores, strategySpot, index) {
  const { appId, contentGroupId, contentId, type } = strategySpot;

  if (appId) {
    return strategyLevelAppToMediaEntry(stores, appId, index);
  }

  if (contentId) {
    return strategyLevelUCToMediaEntry(stores, strategySpot, index);
  }

  if (contentGroupId) {
    return strategyLevelCGToMediaEntry(stores, strategySpot, index);
  }

  if (type === MEDIA_ENTRY_WEBPAGE) {
    return strategyLevelWebpageToMediaEntry(strategySpot, index);
  }

  console.warn("Invalid spot.", strategySpot);
  return {};
}

function normalizeContentGroup(mediaEntry) {
  const { content = [], id, type } = mediaEntry;

  const contentOptions = content.map((c) => {
    const { conflictsWith, duration } = c;
    const options = {};

    if (conflictsWith && conflictsWith.length) {
      options.conflicts_with = conflictsWith.join(",");
    }

    // Duration of image is set at strategy level
    // Videos don't have duration set
    if (isImage(c.mimeType)) {
      options.duration = duration;
    }
    return options;
  });

  return {
    content_options: contentOptions,
    contentGroupId: id,
    id,
    type,
  };
}

function normalizeSpot(mediaEntry) {
  const { conflictsWith, duration, id, name, type, url } = mediaEntry;

  switch (type) {
    case MEDIA_ENTRY_APPLICATION:
      return { appId: id, id, type };
    case MEDIA_ENTRY_CONTENT_GROUP:
      return normalizeContentGroup(mediaEntry);
    case MEDIA_ENTRY_USER_CONTENT:
      return {
        duration,
        id,
        type,
        conflicts_with: conflictsWith ? conflictsWith.join(",") : "",
        contentId: id,
      };
    case MEDIA_ENTRY_WEBPAGE:
      return { duration, type, url, name };
    default:
      console.warn("Unhandled spot type", type);
      break;
  }

  return { id, type };
}

const PropTypePriorities = PropTypes.oneOf([
  PRIORITY_MAIN.id,
  PRIORITY_FALLBACK.id,
]);

function _renderCheckbox(list, id) {
  if (list.length <= 10) {
    return null;
  }

  return (
    <input
      type="checkbox"
      className="read-more-state"
      id={"read-more-checkbox-" + id}
    />
  );
}

function _renderLabel(list, id) {
  if (list.length <= 10) {
    return null;
  }

  return (
    <label htmlFor={"read-more-checkbox-" + id} className="read-more-trigger" />
  );
}

function formatTargets(targets) {
  let tags = [];
  let devices = [];
  const targetsId = uniqueId("targets-");

  targets.forEach(function (target) {
    switch (target.type) {
      case "t":
        tags.push(target);
        break;
      case "d":
        devices.push(target);
        break;
      default:
        break;
    }
  });

  let lis = [];
  for (let [idx, tag] of Object.entries(tags)) {
    lis.push(
      <li key={`t-${idx}`}>
        <Link to={`/tags/${tag.id}`}>{tag.name}</Link>
      </li>,
    );
  }

  for (let [idx, device] of Object.entries(devices)) {
    lis.push(
      <li key={`d-${idx}`}>
        <Link to={`/devices/${device.id}`}>{device.name}</Link>
      </li>,
    );
  }

  return (
    <div>
      {_renderCheckbox(lis, targetsId)}
      <ul className="read-more-wrap list-unstyled">
        {lis.slice(0, 10)}
        <div className="read-more-target">{lis.slice(10)}</div>
      </ul>
      {_renderLabel(lis, targetsId)}
    </div>
  );
}

function formatStrategy(name, row) {
  return (
    <Link to={`/v2/strategies/${row.strategy_id}/${VIEW.EDIT}`}>{name}</Link>
  );
}

function formatDeployDate(date) {
  return utcToLocal(date);
}

function getStrategyOptions(strategies) {
  if (!strategies) {
    return defaultArray;
  }

  return Object.values(strategies)
    .map((strategy) => {
      const { id, name } = strategy;

      return {
        value: id,
        label: name,
      };
    })
    .sort(sortByValueAsc);
}

export {
  denormalizeStrategy,
  formatDeployDate,
  formatStrategy,
  formatTargets,
  getStrategyOptions,
  normalizeSpot,
  normalizeStrategy,
  PropTypePriorities,
  spotToMediaEntry,
};
